import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ImageAssetWithAltText } from '@fhs/ui';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { PICKER_SELECTION_TYPE } from '../components/guide/constants';
import type { PickerSelectionType, Step } from '../components/guide/types';
import { useMenuGuideQuery } from '../queries/use-menu-guide-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import type {
  CustomizationSelections,
  EditComboItem,
  MenuPickerData,
  SelectedCustomization,
} from '../types';
import { buildDefaultComboSelectedPickers } from '../utils/combo-selection-readiness';

import { useMenuPicker } from './menu-picker';

export type SelectedPicker = {
  id: string;
  slug: string;
  // Derived
  displayName: string;
  priceCents: number;
  basePriceCents?: number;
  baseCalories?: number;

  // Instructions
  instructions?: string;

  // Picker
  pickerSelections?: Record<string, string | null>;

  // PickerAspectOption
  selectedPickerAspectOptionId?: string;
  selectedPickerAspectOption?:
    | null
    | NonNullable<MenuPickerData['pickerAspect']>['options'][number];

  // Customizations
  defaultCustomizationSelections?: CustomizationSelections;
  userCustomizationSelections?: CustomizationSelections;
  resolvedCustomizationSelections?: CustomizationSelections;

  draftCustomizationSelections?: CustomizationSelections;
  requiresCustomization: boolean;

  // Formatted customization options to render on "Make it a combo" section
  selectedCustomizationOptions?: SelectedCustomization[];

  // UI
  image?: ImageAssetWithAltText;
};

export type ComboSelectionContext = {
  isComboEnabled: boolean;
  isComboAvailable: boolean;
  selectedPickers: Record<string, SelectedPicker>;
  isEditingPickers: boolean;
  setIsComboEnabled: (enabled: boolean) => void;
  setSelectedPickers: (pickers: Record<string, SelectedPicker>) => void;
  addOrUpdateSelectedPicker: (step: string, picker: SelectedPicker) => void;
  setIsEditingPickers: (editing: boolean) => void;
  isComboGuideOpen: boolean;
  setIsComboGuideOpen: (value: boolean) => void;
  shouldAddToCartDirectly: boolean;
  setShouldAddToCartDirectly: (value: boolean) => void;
  comboModalDisplayed: boolean;
  setComboModalDisplayed: (value: boolean) => void;
  guideSteps: Array<Step>;
  isGuideLoading: boolean;
  pickerSelectionType: PickerSelectionType;
  setPickerSelectionType: (value: PickerSelectionType) => void;
};

type ComboSelectionProviderStateProps = {
  children?: ReactNode;
  guideSteps: Array<Step>;
  isGuideLoading: boolean;
  editComboItems?: EditComboItem[];
  isEditComboItemsLoading?: boolean;
  initialState?: {
    isComboEnabled?: boolean;
    isComboAvailable?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};

const comboSelectionContext = createContext<ComboSelectionContext | null>(null);

const ComboSelectionProviderState = ({
  children,
  initialState,
  guideSteps,
  isGuideLoading,
  editComboItems,
  isEditComboItemsLoading,
}: ComboSelectionProviderStateProps) => {
  const enableComboDefaultSelection = useFlag('enable-menu-details-default-combo-selection');

  const getInitialPickerSelectionType = () => {
    if (initialState && initialState.isComboEnabled !== undefined) {
      return initialState.isComboEnabled
        ? PICKER_SELECTION_TYPE.COMBO
        : PICKER_SELECTION_TYPE.JUST_SUB;
    }

    return enableComboDefaultSelection
      ? PICKER_SELECTION_TYPE.COMBO
      : PICKER_SELECTION_TYPE.JUST_SUB;
  };

  const [pickerSelectionType, setPickerSelectionType] = useState(getInitialPickerSelectionType);

  useEffect(() => {
    if (initialState?.isComboAvailable === false) {
      setPickerSelectionType(PICKER_SELECTION_TYPE.JUST_SUB);
    }
  }, [initialState?.isComboAvailable]);

  const defaultSelectedPickers = useMemo(
    () =>
      guideSteps.length > 0 && !isEditComboItemsLoading
        ? buildDefaultComboSelectedPickers(guideSteps, editComboItems)
        : {},
    [guideSteps, editComboItems, isEditComboItemsLoading]
  );

  const [selectedPickers, setSelectedPickers] = useState<Record<string, SelectedPicker>>(() => {
    const existingSelectedPickers = initialState?.comboSelectedPickers;
    if (existingSelectedPickers && Object.keys(existingSelectedPickers).length > 0) {
      return existingSelectedPickers;
    }

    return defaultSelectedPickers;
  });

  useEffect(() => {
    if (!guideSteps.length || isEditComboItemsLoading) {
      return;
    }

    setSelectedPickers(prev => {
      const hasMissingDefaults = Object.keys(defaultSelectedPickers).some(
        slotId => !prev[slotId]?.id
      );

      if (!hasMissingDefaults && Object.keys(prev).length > 0) {
        return prev;
      }

      if (
        Object.keys(defaultSelectedPickers).length === 0 &&
        Object.keys(prev).length === 0
      ) {
        return prev;
      }

      // User selections win over defaults; fill in any slots that are still empty.
      return { ...defaultSelectedPickers, ...prev };
    });
  }, [guideSteps, defaultSelectedPickers, isEditComboItemsLoading]);
  const [isEditingPickers, setIsEditingPickers] = useState(false);
  const [isComboGuideOpen, setIsComboGuideOpen] = useState(false);
  const [shouldAddToCartDirectly, setShouldAddToCartDirectly] = useState(false);

  // Used to not display "Make it a Combo" modal more than once if user already opted for just the sub
  const [comboModalDisplayed, setComboModalDisplayed] = useState(false);

  const isComboEnabled = pickerSelectionType === PICKER_SELECTION_TYPE.COMBO;
  const isComboAvailable = initialState?.isComboAvailable ?? true;
  const isGuideHydrating =
    isGuideLoading ||
    Object.keys(defaultSelectedPickers).some(slotId => !selectedPickers[slotId]?.id);

  const addOrUpdateSelectedPicker = useCallback(
    (step: string, picker: SelectedPicker) => {
      setSelectedPickers(prevPickers => ({
        ...prevPickers,
        [step]: picker, // Add or replace the picker for the given step
      }));
    },
    [setSelectedPickers]
  );

  const value: ComboSelectionContext = {
    selectedPickers,
    setSelectedPickers,
    addOrUpdateSelectedPicker,
    isEditingPickers,
    setIsEditingPickers,
    isComboEnabled,
    isComboAvailable,
    setIsComboEnabled: () => {}, // TODO: remove/handle this on FHS-4323
    isComboGuideOpen,
    setIsComboGuideOpen,
    shouldAddToCartDirectly,
    setShouldAddToCartDirectly,
    comboModalDisplayed,
    setComboModalDisplayed,
    guideSteps,
    isGuideLoading: isGuideHydrating,
    pickerSelectionType,
    setPickerSelectionType,
  };

  return <comboSelectionContext.Provider value={value}>{children}</comboSelectionContext.Provider>;
};

type ComboSelectionProviderProps = {
  children: ReactNode;
  resetKey?: string;
  initialState?: {
    isComboEnabled?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
  // Combo items belonging to the cart entry currently being edited (edit-from-cart flow).
  // Pass `undefined` when not editing, or while the cart entry's combo items are still loading.
  editComboItems?: EditComboItem[];
  isEditComboItemsLoading?: boolean;
};
export const ComboSelectionProvider = ({
  initialState,
  children,
  resetKey,
  editComboItems,
  isEditComboItemsLoading,
}: ComboSelectionProviderProps) => {
  const { query: menuPickerQuery } = useMenuPicker();
  const comboId = menuPickerQuery.data?.combo?.id ?? '';

  const menuGuideQuery = useMenuGuideQuery({
    enabled: Boolean(comboId),
    comboId,
    ...useMenuQueryOpts(),
  });

  const isComboRequired = menuPickerQuery.data?.combo?.isRequired;
  const isGuideLoading =
    Boolean(comboId) && !menuGuideQuery.isFetched && !menuGuideQuery.isError;

  /**
   * If the menu guide is not available, it might be due to issues like missing or misconfigured combo options.
   * In this case, we assume combos should not be enabled to prevent broken configurations from affecting the UX.
   */
  const isComboMenuGuideAvailable =
    !comboId || isGuideLoading ? true : Boolean(menuGuideQuery.data?.isAvailable);

  // We want to force combo enabled in case it's required (e.g. Kids Combos)
  const currentComboState = isComboRequired ? true : initialState?.isComboEnabled;

  const mergedInitialState = {
    ...initialState,
    // We want to force combo enabled in case it's required (e.g. Kids Combos)
    isComboEnabled: currentComboState,
    // Disable combos if the menu guide is not available, otherwise preserve or enforce the current state.
    isComboAvailable: isComboMenuGuideAvailable,
  };

  // Prefer a route-derived key, which is available before menu data loads. Falling back to the
  // async combo id is useful for provider consumers that do not have a stable item key.
  const providerKey = [resetKey ?? comboId, initialState && JSON.stringify(initialState)]
    .filter(Boolean)
    .join('-');

  return (
    <ComboSelectionProviderState
      initialState={mergedInitialState}
      guideSteps={menuGuideQuery.data?.steps ?? []}
      isGuideLoading={isGuideLoading}
      editComboItems={editComboItems}
      isEditComboItemsLoading={isEditComboItemsLoading}
      // Reset combo selection state when the item or caller-provided initial state changes.
      key={providerKey}
    >
      {children}
    </ComboSelectionProviderState>
  );
};

export const useComboSelectionContext = (): ComboSelectionContext => {
  const context = useContext(comboSelectionContext);

  if (!context) {
    throw new Error(
      'useComboSelectionContext can only be called from within a descendant of ComboSelectionProvider'
    );
  }

  return context;
};
